/**
 * @fileoverview Mobile detection hooks for responsive behavior and device-specific features
 * @module hooks/use-mobile
 */

import * as React from "react"

const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

/**
 * Extended mobile detection information.
 *
 * Provides detailed information about the user's device and browsing context
 * to enable mobile-specific UX optimizations.
 */
export interface MobileInfo {
  /** Screen width is below mobile breakpoint (768px) */
  isMobile: boolean;
  /** Actual mobile device detected via user agent */
  isMobileDevice: boolean;
  /** Running inside a wallet app's in-app browser (MetaMask, Trust Wallet, etc.) */
  isInWalletBrowser: boolean;
  /** Device category based on user agent and screen size */
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

/**
 * Detects if the user agent indicates a mobile device.
 *
 * @returns True if user agent matches mobile patterns
 */
function detectMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Detects if the user agent indicates a tablet device.
 *
 * @returns True if user agent matches tablet patterns
 */
function detectTablet(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);
}

/**
 * Detects if the browser is running inside a wallet app's in-app browser.
 *
 * Checks for common wallet browser indicators:
 * - MetaMask mobile browser
 * - Trust Wallet browser
 * - Coinbase Wallet browser
 * - Other Web3 wallet browsers
 *
 * @returns True if running inside a wallet app browser
 */
function detectWalletBrowser(): boolean {
  if (typeof window === 'undefined') return false;

  const ethereum = (window as unknown as { ethereum?: {
    isMetaMask?: boolean;
    isTrust?: boolean;
    isCoinbaseWallet?: boolean;
    isWalletConnect?: boolean;
  } }).ethereum;

  if (!ethereum) return false;

  // Check for common wallet browser indicators
  return !!(
    ethereum.isMetaMask ||
    ethereum.isTrust ||
    ethereum.isCoinbaseWallet
  );
}

/**
 * Determines the device type based on user agent and screen size.
 *
 * @param screenWidth - Current screen width in pixels
 * @returns Device category
 */
function getDeviceType(screenWidth: number): 'desktop' | 'mobile' | 'tablet' {
  const isTabletUA = detectTablet();
  const isMobileUA = detectMobileDevice();

  // User agent detection takes priority for device type
  if (isTabletUA) return 'tablet';
  if (isMobileUA && screenWidth < TABLET_BREAKPOINT) return 'mobile';
  if (screenWidth < MOBILE_BREAKPOINT) return 'mobile';
  if (screenWidth < TABLET_BREAKPOINT) return 'tablet';
  return 'desktop';
}

/**
 * Simple hook to detect if the screen is mobile-sized.
 *
 * Uses media query for responsive screen width detection.
 * Returns false during SSR and initial hydration.
 *
 * @returns True if screen width is below mobile breakpoint
 *
 * @example
 * ```tsx
 * function ResponsiveComponent() {
 *   const isMobile = useIsMobile();
 *   return isMobile ? <MobileView /> : <DesktopView />;
 * }
 * ```
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = (): void => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}

/**
 * Extended mobile detection hook with device type and wallet browser detection.
 *
 * Provides comprehensive information about the user's device and browsing context:
 * - Screen size detection (responsive)
 * - User agent device detection (mobile/tablet/desktop)
 * - Wallet in-app browser detection (MetaMask, Trust Wallet, etc.)
 *
 * Use this hook when you need to provide different UX for mobile users,
 * especially for wallet connection flows.
 *
 * @returns Mobile detection information
 *
 * @example
 * ```tsx
 * function WalletConnect() {
 *   const { isMobileDevice, isInWalletBrowser } = useMobileInfo();
 *
 *   if (isMobileDevice && !isInWalletBrowser) {
 *     return <OpenInWalletAppPrompt />;
 *   }
 *
 *   return <NormalConnectFlow />;
 * }
 * ```
 */
export function useMobileInfo(): MobileInfo {
  const [info, setInfo] = React.useState<MobileInfo>({
    isMobile: false,
    isMobileDevice: false,
    isInWalletBrowser: false,
    deviceType: 'desktop',
  });

  React.useEffect(() => {
    const updateInfo = (): void => {
      const screenWidth = window.innerWidth;
      const isMobileDevice = detectMobileDevice();
      const isInWalletBrowser = isMobileDevice && detectWalletBrowser();

      setInfo({
        isMobile: screenWidth < MOBILE_BREAKPOINT,
        isMobileDevice,
        isInWalletBrowser,
        deviceType: getDeviceType(screenWidth),
      });
    };

    // Initial detection
    updateInfo();

    // Listen for resize to update screen-based detection
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    mql.addEventListener("change", updateInfo);

    return () => mql.removeEventListener("change", updateInfo);
  }, []);

  return info;
}
